import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import Events, { EVENTS } from '@fogia/core-web/config/events';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { debugHelper } from '@fogia/core-web/utils/debugHelper';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type ReEvaluateHeaderStateParameter = {
    headerState?: HeaderContextState;
    withStateChanges?: Partial<HeaderContextState>;
};

type ReEvaluateHeaderState = (parameter?: ReEvaluateHeaderStateParameter) => void;

export type HeaderContextState = typeof initialHeaderContextState;

export type HeaderContext = typeof initialHeaderContext;

const setHeaderCssVars = (state: HeaderContextState, device: 'mobile' | 'desktop') => {
    const rootElement = document.querySelector(':root');
    if (!rootElement) {
        return;
    }
    const { endConsoleGroup } = debugHelper({
        context: 'HeaderContext',
        message: 'setHeaderCssVars:init',
        options: {
            createConsoleMessageGroup: { label: 'setHeaderCssVars' },
        },
        additionalParameters: {
            state,
            device,
        },
    });

    const updates: string[] = [];

    [
        // {property: "", value: ""},
        { property: '--header-height', value: `${state.height[device]}px` },
        {
            property: '--header-top-position',
            value: state.isShowing ? '0' : `-${state.height[device]}px`,
        },
        {
            property: '--header-text-color',
            value: state.isTransparent ? 'var(--color-base-white)' : 'var(--color-base-charcoal)',
        },
        {
            property: '--header-background-color',
            value: state.isTransparent ? 'var(--color-transparent)' : 'var(--color-base-brickwork-orange)',
        },
    ].forEach(({ property, value }) => {
        // @ts-expect-error: Reason:  We know that style exists.
        const currentValue = rootElement.style.getPropertyValue(property);
        if (currentValue !== value) {
            // @ts-expect-error: Reason:  We know that style exists.
            rootElement.style.setProperty(property, value);
            updates.push(`${property}: ${currentValue} => ${value}`);

            debugHelper({
                context: 'HeaderContext',
                message: `setHeaderCssVars:property > ${property}`,
                additionalParameters: {
                    currentValue,
                    newValue: value,
                },
            });
        }
    });

    debugHelper({
        context: 'HeaderContext',
        message: `setHeaderCssVars:complete`,
        additionalParameters: { updates },
    });

    endConsoleGroup?.();
};

/** Scroll threshold (px): header is considered "at top" (transparent) when scrollY <= this. */
const TRANSPARENT_SCROLL_THRESHOLD = 120;

const HEADER_OPTIONS = {
    SCROLL_DEBOUNCE_TIMER: 300,
};

const HEADER_OVERRIDES: Record<string, Partial<HeaderContextState>> = {};

const initialHeaderContextState = {
    isTransparent: true,
    isShowing: true,

    height: {
        mobile: 50, // 30 (content) + 10 (top padding) + 10 (bottom padding)
        desktop: 60, // 40 (content) + 10 (top padding) + 10 (bottom padding)
    },
};

const initialHeaderContext = {
    headerState: initialHeaderContextState,
    setOverride: (context: string, stateReplacement?: Partial<HeaderContextState>) => {},
    reEvaluateHeaderState: (parameter: ReEvaluateHeaderStateParameter) => {},
};

const HeaderContext = createContext(initialHeaderContext);
export const useHeaderContext = () => useContext(HeaderContext);

interface HeaderContextProviderProps {
    children: React.ReactNode;
}

export const HeaderContextProvider = ({ children }: HeaderContextProviderProps) => {
    const pageType = useAppSelector(({ page }) => page?.type ?? '');
    const pageTemplate = useAppSelector(({ page }) => page?.template);
    const overlayActive = !!useAppSelector(({ application }) => application.overlay.view || ''); // @TODO Move to header overlay?
    const isMobile = useBreakpoint({ to: 'desktop.sm' });
    const currentDevice = isMobile ? 'mobile' : 'desktop';
    const prevScrollRef = useRef(0);
    // Banner removed – header has no banner, so banner height is always 0
    const [state, setState] = useState({
        ...initialHeaderContextState,
        banner: {
            height: { mobile: 0, desktop: 0 },
        },
    });
    // The scroll handler is set up once and never re-registered, so it would otherwise keep seeing the
    // state from the first render (e.g. isShowing: true). We store current state/device in refs and read
    // from them so the handler and reEvaluateHeaderState always use up-to-date values for runImmediately and hasChanges.
    const stateRef = useRef(state);
    const currentDeviceRef = useRef(currentDevice);
    stateRef.current = state;
    currentDeviceRef.current = currentDevice;

    // @TODO Make this a useCallback?
    const reEvaluateHeaderState: ReEvaluateHeaderState = useCallback(
        ({ headerState = stateRef.current, withStateChanges = {} } = {}) => {
            // Default to stateRef so when called from the scroll handler (stale closure) we still read latest state.
            const device = currentDeviceRef.current;
            const { endConsoleGroup } = debugHelper({
                context: 'HeaderContext',
                message: 'reEvaluateHeaderState:Init',
                options: {
                    createConsoleMessageGroup: {
                        label: 'reEvaluateHeaderState',
                    },
                },
                additionalParameters: {
                    currentState: stateRef.current,
                    evaluationState: headerState,
                    withStateChanges,
                    currentDevice: device,
                    HEADER_OVERRIDES,
                },
            });

            const currentScrollYOffset = window?.scrollY || 0;
            const headerHeight = headerState.height[device];
            const prevScroll = prevScrollRef.current;
            const nearTop = currentScrollYOffset < headerHeight;
            // Use strict < so we only show when actually scrolling up; if user stops (current === prev) we don't show.
            const scrolledUp = currentScrollYOffset < prevScroll;
            const isShowing = nearTop || scrolledUp;

            const isTransparent = currentScrollYOffset <= TRANSPARENT_SCROLL_THRESHOLD;
            let newState = {
                ...headerState,
                isTransparent,
                isShowing,
                ...withStateChanges,
            };
            debugHelper({
                context: 'HeaderContext',
                message: `reEvaluateHeaderState:evaluation`,
                additionalParameters: {
                    isTransparent: {
                        currentScrollYOffset,
                        threshold: TRANSPARENT_SCROLL_THRESHOLD,
                    },
                    isShowing: {
                        currentScrollYOffset,
                        headerHeight: headerState.height[device],
                        oldScrollPos: prevScrollRef.current,
                        nearTop,
                        scrolledUp,
                    },
                    newState,
                },
            });

            // Run the overrides from other sources.
            Object.keys(HEADER_OVERRIDES).forEach((overrideContext) => {
                const overrideContextState = HEADER_OVERRIDES[overrideContext];
                newState = {
                    ...newState,
                    ...overrideContextState,
                };
                debugHelper({
                    context: 'HeaderContext',
                    message: `reEvaluateHeaderState:evaluationOverrides`,
                    additionalParameters: {
                        newState,
                        overrideContextState,
                        overrideContext,
                    },
                });
            });

            // Compare against stateRef so we detect changes when called from the (stale-closure) scroll handler.
            const currentState = stateRef.current;
            const hasChanges = JSON.stringify(newState) !== JSON.stringify(currentState); // @TODO Improve this with something else than json compare?

            debugHelper({
                context: 'HeaderContext',
                message: `reEvaluateHeaderState:State Differences? (${hasChanges.toString()})`,
                additionalParameters: {
                    headerState,
                    newState,
                },
            });

            // Don't set a new state if nothing has changed since it would trigger a re-render.
            if (hasChanges) {
                debugHelper({
                    context: 'HeaderContext',
                    message: `reEvaluateHeaderState:setState`,
                    additionalParameters: newState,
                });
                setState(newState);
            }

            endConsoleGroup?.();
        },
        [state, pageType, pageTemplate, prevScrollRef.current],
    );

    const setOverride = useCallback(
        (context: string, stateReplacement?: Partial<HeaderContextState>, evaluateState = true) => {
            const { endConsoleGroup } = debugHelper({
                context: 'HeaderContext',
                message: 'setOverride:Init',
                options: {
                    createConsoleMessageGroup: { label: 'setOverride' },
                },
                additionalParameters: {
                    context,
                    stateReplacement,
                    evaluateState,
                    HEADER_OVERRIDES,
                },
            });

            if (stateReplacement) {
                HEADER_OVERRIDES[context] = stateReplacement;
                debugHelper({
                    context: 'HeaderContext',
                    message: `setOverride:Added ${context}`,
                    additionalParameters: stateReplacement,
                });
            } else {
                delete HEADER_OVERRIDES[context];
                debugHelper({
                    context: 'HeaderContext',
                    message: `setOverride:Removed ${context}`,
                });
            }

            if (evaluateState) {
                reEvaluateHeaderState();
            }

            endConsoleGroup?.();
        },
        [state, pageType, pageTemplate, prevScrollRef.current, reEvaluateHeaderState],
    );

    let scrollDebounceHandle: ReturnType<typeof setTimeout> | undefined;
    useEffect(() => {
        const { endConsoleGroup } = debugHelper({
            context: 'HeaderContext',
            message: 'onMount:init',
            options: {
                createConsoleMessageGroup: { label: 'onMount' },
            },
        });

        // @TODO if the height changes, re-set the height
        // const resizeEventHandle = Events.subscribe(
        //     EVENTS.WINDOW.RESIZE,
        //     debounce((ev) => {
        //         // setHeaderCssVars(state.height[currentDevice], state.banner.height[currentDevice]);
        //         console.log("resize", { state });
        //     }, state.debounce),
        // );

        const onWindowScroll = () => Events.trigger(EVENTS.WINDOW.SCROLL);
        window.addEventListener('scroll', onWindowScroll, { passive: true });

        const scrollEventHandle = Events.subscribe(EVENTS.WINDOW.SCROLL, () => {
            (async () => {
                const currentScrollYOffset = window?.scrollY || 0;
                const latestState = stateRef.current;
                const device = currentDeviceRef.current;
                const isScrollingUp = currentScrollYOffset < prevScrollRef.current;
                const nearTop = currentScrollYOffset <= latestState.height[device];
                const scrollDownAndShowing = currentScrollYOffset > prevScrollRef.current && latestState.isShowing;
                // Run sync when we need fast response (near top, scroll down + header visible, or scroll up).
                // Otherwise a 1ms timeout would keep getting cleared by new scroll events and never fire.
                const runImmediately = nearTop || scrollDownAndShowing || isScrollingUp;

                if (!scrollDebounceHandle) {
                    debugHelper({
                        context: 'HeaderContext',
                        message: 'scrollEvaluation:init',
                    });
                }

                // Clear the earlier timeout if we have one, we will create a new one.
                if (scrollDebounceHandle) {
                    clearTimeout(scrollDebounceHandle);
                }

                if (runImmediately) {
                    // Run sync so slow scroll doesn't keep resetting a 1ms timeout and never firing
                    if (scrollDebounceHandle) {
                        clearTimeout(scrollDebounceHandle);
                        scrollDebounceHandle = undefined;
                    }
                    reEvaluateHeaderState();
                    prevScrollRef.current = window?.scrollY || 0;
                } else {
                    scrollDebounceHandle = setTimeout(() => {
                        const currentScrollYOffset = window?.scrollY || 0;
                        const { endConsoleGroup } = debugHelper({
                            context: 'HeaderContext',
                            message: 'scrollEvaluation:debounceCallback:run',
                            options: {
                                createConsoleMessageGroup: {
                                    label: 'scrollEvaluation:debounceCallback',
                                },
                            },
                        });

                        reEvaluateHeaderState();

                        debugHelper({
                            context: 'HeaderContext',
                            message: `scrollEvaluation:setNewScrollPosition (${prevScrollRef.current} => ${currentScrollYOffset})`,
                        });
                        prevScrollRef.current = currentScrollYOffset;
                        scrollDebounceHandle = undefined;

                        endConsoleGroup?.();
                    }, HEADER_OPTIONS.SCROLL_DEBOUNCE_TIMER);
                }

                // Always update prev so the next scroll event has the correct "previous" position. If we took the
                // debounce branch we don't run reEvaluateHeaderState until timeout fires, so prev would otherwise
                // stay stale and isScrollingUp would be false when the user scrolls up (e.g. prev stuck at 78).
                prevScrollRef.current = currentScrollYOffset;
            })();
        });

        // Ends the onMount logs.
        endConsoleGroup?.();

        return () => {
            const { endConsoleGroup } = debugHelper({
                context: 'HeaderContext',
                message: 'unMount:init',
                options: {
                    createConsoleMessageGroup: { label: 'unMount' },
                },
            });

            window.removeEventListener('scroll', onWindowScroll);
            // Events.unsubscribe(EVENTS.WINDOW.RESIZE, resizeEventHandle);
            Events.unsubscribe(EVENTS.WINDOW.SCROLL, scrollEventHandle);

            // Ends the unMount logs
            endConsoleGroup?.();
        };
    }); // @TODO Add deps and add re-eval? Right now this triggers extra re-renders, but the functions are not updated correctly if so.

    // If a view is updated, we need to re-evaluate the header
    useEffect(() => {
        debugHelper({ context: 'HeaderContext', message: 'view changes' });
        reEvaluateHeaderState();
    }, [pageType, pageTemplate]);

    useIsoLayoutEffect(() => {
        setHeaderCssVars(state, currentDevice);
    }, [state, currentDevice]);

    debugHelper({
        context: 'HeaderContext',
        message: 'Render',
        additionalParameters: {
            state,
            pageType,
            pageTemplate,
            overlayActive,
            isMobile,
            options: HEADER_OPTIONS,
            overrides: HEADER_OVERRIDES,
        },
    });

    // const headerContextProviderProps = useMemo<HeaderContext>(
    //     () => ({
    //         headerState: state,
    //         reEvaluateHeaderState,
    //         setOverride,
    //     }),
    //     [state],
    // );
    const headerContextProviderProps = {
        headerState: state,
        reEvaluateHeaderState,
        setOverride,
    };

    return <HeaderContext.Provider value={headerContextProviderProps}>{children}</HeaderContext.Provider>;
};

export const useOverrideHeaderContext = (context: string, override: Partial<HeaderContextState>) => {
    const { setOverride } = useHeaderContext();
    useEffect(() => {
        setOverride(context, override);

        return () => {
            setOverride(context);
        };
    }, []);
};
